'use client';
import React, { useState, useRef, useEffect } from 'react';

const RECIPIENT = 'u1vy8eux5xpqd7hj4f8gzhsn7qcs55xecdys07psc4s6ncs2rkls56sphd52833f88kuy9zqkzmtsqev6kc6hftmlnnhkl08yzeyqky9tt';
const CIPHERSCAN_API = 'https://api.mainnet.cipherscan.app/api';

interface ShieldedBuyModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemName: string;
  priceZec: string;
  priceUsd?: string;
}

type ConfirmState = 'idle' | 'polling' | 'confirmed' | 'not_found' | 'error';

export default function ShieldedBuyModal({ isOpen, onClose, itemName, priceZec, priceUsd }: ShieldedBuyModalProps) {
  const [shieldedAddr, setShieldedAddr] = useState('');
  const [txid, setTxid] = useState('');
  const [copied, setCopied] = useState(false);
  const [addrError, setAddrError] = useState('');
  const [txidError, setTxidError] = useState('');
  const [confirmState, setConfirmState] = useState<ConfirmState>('idle');
  const [confirmations, setConfirmations] = useState(0);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [done, setDone] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(RECIPIENT).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    return () => stopPolling();
  }, []);

  const pollTx = async (txidVal: string, attempt: number): Promise<void> => {
    try {
      // First check mempool
      const mempoolRes = await fetch(`${CIPHERSCAN_API}/mempool/tx/${txidVal}`);
      if (mempoolRes.ok) {
        // In mempool = 0 confirmations, still pending
        setPollAttempts(attempt);
        setConfirmations(0);
        return;
      }

      // Check confirmed tx
      const txRes = await fetch(`${CIPHERSCAN_API}/tx/${txidVal}`);
      if (txRes.ok) {
        const data = await txRes.json();
        const confs: number = data?.confirmations ?? data?.confirmation_count ?? 0;
        setConfirmations(confs);
        setPollAttempts(attempt);
        if (confs >= 1) {
          stopPolling();
          setConfirmState('confirmed');
        }
        return;
      }

      // 404 = not found yet
      if (txRes.status === 404) {
        setPollAttempts(attempt);
        setConfirmState('not_found');
        return;
      }

      setPollAttempts(attempt);
    } catch {
      setPollAttempts(attempt);
      // Network error — keep polling silently
    }
  };

  const handleConfirm = () => {
    let valid = true;
    if (!shieldedAddr.trim()) {
      setAddrError('Please enter your shielded Zcash address.');
      valid = false;
    } else if (!shieldedAddr.trim().startsWith('u1') && !shieldedAddr.trim().startsWith('zs')) {
      setAddrError('Address must start with u1 (unified) or zs (sapling).');
      valid = false;
    } else {
      setAddrError('');
    }

    if (!txid.trim()) {
      setTxidError('Please enter the transaction ID from your wallet.');
      valid = false;
    } else if (!/^[0-9a-fA-F]{64}$/.test(txid.trim())) {
      setTxidError('Transaction ID must be a 64-character hex string.');
      valid = false;
    } else {
      setTxidError('');
    }

    if (!valid) return;

    setConfirmState('polling');
    setConfirmations(0);
    setPollAttempts(0);

    let attempt = 0;
    pollTx(txid.trim(), attempt);

    pollRef.current = setInterval(() => {
      attempt += 1;
      pollTx(txid.trim(), attempt);
    }, 8000);
  };

  const handleClose = () => {
    stopPolling();
    setShieldedAddr('');
    setTxid('');
    setAddrError('');
    setTxidError('');
    setConfirmState('idle');
    setConfirmations(0);
    setPollAttempts(0);
    setDone(false);
    onClose();
  };

  if (!isOpen) return null;

  if (done) {
    return (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(10,10,11,.88)',
          backdropFilter: 'blur(12px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
          animation: 'fadeIn .2s ease',
        }}
        onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
      >
        <div style={{
          background: 'var(--bg-2)',
          border: '1px solid rgba(74,222,128,.3)',
          borderRadius: 20,
          padding: 'clamp(22px,3vw,32px)',
          width: '100%', maxWidth: 440,
          position: 'relative',
          textAlign: 'center',
          animation: 'fadeIn .25s ease',
        }}>
          <button onClick={handleClose} style={{ position: 'absolute', top: 16, right: 16, width: 32, height: 32, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--bg-3)', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(74,222,128,.12)', border: '2px solid rgba(74,222,128,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 32 }}>✓</div>
          <div style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 22, marginBottom: 8, color: '#4ade80' }}>Purchase Confirmed!</div>
          <div style={{ fontSize: 13, color: 'var(--text-faint)', lineHeight: 1.7, marginBottom: 8 }}>
            Your transaction is on-chain. <span style={{ color: 'var(--text)', fontWeight: 500 }}>{itemName}</span> will be transferred to your shielded address.
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--mono)', fontSize: 11, color: '#4ade80', background: 'rgba(74,222,128,.08)', border: '1px solid rgba(74,222,128,.25)', borderRadius: 999, padding: '5px 14px', marginBottom: 24 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
            {confirmations} confirmation{confirmations !== 1 ? 's' : ''} · On-chain
          </div>
          <button onClick={handleClose} style={{ width: '100%', padding: '13px 0', fontSize: 14, fontWeight: 700, borderRadius: 12, border: 'none', cursor: 'pointer', background: 'var(--gold)', color: '#1a1003', fontFamily: 'var(--display)' }}>
            Done 🎉
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(10,10,11,.88)',
        backdropFilter: 'blur(12px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        animation: 'fadeIn .2s ease',
      }}
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div style={{
        background: 'var(--bg-2)',
        border: '1px solid rgba(244,183,40,.25)',
        borderRadius: 20,
        padding: 'clamp(22px,3vw,32px)',
        width: '100%',
        maxWidth: 480,
        position: 'relative',
        boxShadow: '0 24px 80px rgba(0,0,0,.6)',
        animation: 'fadeIn .25s ease',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        <button
          onClick={handleClose}
          style={{
            position: 'absolute', top: 16, right: 16,
            width: 32, height: 32, borderRadius: 8,
            border: '1px solid var(--line)',
            background: 'var(--bg-3)',
            color: 'var(--text-dim)',
            cursor: 'pointer', fontSize: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >×</button>

        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '1px',
            color: 'var(--gold)', border: '1px solid rgba(244,183,40,.28)',
            borderRadius: 999, padding: '4px 12px',
            background: 'rgba(244,183,40,.06)', marginBottom: 14,
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <path d="M12 2l8 3.5V11c0 5-3.4 8.6-8 10-4.6-1.4-8-5-8-10V5.5z" />
            </svg>
            BUY WITH SHIELDED ZEC
          </div>
          <h2 style={{ margin: '0 0 4px', fontSize: 20, fontFamily: 'var(--display)', fontWeight: 700 }}>{itemName}</h2>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 20, fontWeight: 700, color: 'var(--gold)' }}>{priceZec} ZEC</span>
            {priceUsd && <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-faint)' }}>~${priceUsd} USD</span>}
          </div>
        </div>

        {/* Step 1: Your address */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '1px', color: 'var(--text-faint)', marginBottom: 8 }}>
            YOUR SHIELDED ZCASH ADDRESS
          </label>
          <textarea
            value={shieldedAddr}
            onChange={e => { setShieldedAddr(e.target.value); setAddrError(''); }}
            placeholder="u1... or zs1..."
            rows={3}
            style={{
              width: '100%', resize: 'none',
              background: 'var(--bg-3)',
              border: addrError ? '1px solid #f87171' : '1px solid var(--line-2)',
              borderRadius: 10, padding: '12px 14px',
              fontFamily: 'var(--mono)', fontSize: 12,
              color: 'var(--text)', outline: 'none',
              lineHeight: 1.6, boxSizing: 'border-box',
            }}
          />
          {addrError && <div style={{ marginTop: 5, fontFamily: 'var(--mono)', fontSize: 11, color: '#f87171' }}>{addrError}</div>}
        </div>

        {/* Step 2: Send to address */}
        <div style={{
          background: 'rgba(244,183,40,.06)',
          border: '1px solid rgba(244,183,40,.18)',
          borderRadius: 12, padding: '14px 16px', marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-faint)', letterSpacing: '1px' }}>SEND {priceZec} ZEC TO</span>
          </div>
          <div style={{
            background: 'var(--bg-3)', border: '1px solid var(--line)',
            borderRadius: 10, padding: '10px 12px',
            display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8,
          }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-2)', wordBreak: 'break-all', lineHeight: 1.7, flex: 1 }}>
              {RECIPIENT}
            </div>
            <button
              onClick={handleCopy}
              style={{
                flexShrink: 0, padding: '6px 12px',
                borderRadius: 8, border: '1px solid var(--line-2)',
                background: copied ? 'rgba(74,222,128,.1)' : 'var(--bg-4)',
                color: copied ? '#4ade80' : 'var(--text-2)',
                fontFamily: 'var(--mono)', fontSize: 10,
                cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'all .15s',
              }}
            >
              {copied ? '✓ COPIED' : 'COPY'}
            </button>
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'rgba(228,228,229,.35)', lineHeight: 1.5 }}>
            🛡️ You just need your shielded address · No wallet required · No wallet connection required · Shielded on Zcash
          </div>
        </div>

        {/* Step 3: Transaction ID */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '1px', color: 'var(--text-faint)', marginBottom: 8 }}>
            TRANSACTION ID (from your wallet after sending)
          </label>
          <input
            type="text"
            value={txid}
            onChange={e => { setTxid(e.target.value); setTxidError(''); }}
            placeholder="64-character hex txid from your Zcash wallet"
            style={{
              width: '100%',
              background: 'var(--bg-3)',
              border: txidError ? '1px solid #f87171' : '1px solid var(--line-2)',
              borderRadius: 10, padding: '12px 14px',
              fontFamily: 'var(--mono)', fontSize: 11,
              color: 'var(--text)', outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          {txidError && <div style={{ marginTop: 5, fontFamily: 'var(--mono)', fontSize: 11, color: '#f87171' }}>{txidError}</div>}
          <div style={{ marginTop: 6, fontFamily: 'var(--mono)', fontSize: 10, color: 'rgba(228,228,229,.3)', lineHeight: 1.5 }}>
            After sending ZEC from your wallet, paste the transaction ID here to verify on-chain.
          </div>
        </div>

        {/* Confirm button */}
        <div style={{ display: 'flex', gap: 10, marginBottom: confirmState !== 'idle' ? 16 : 0 }}>
          <button
            onClick={handleClose}
            style={{
              flex: 1, padding: '12px 0', fontSize: 14, borderRadius: 12,
              cursor: 'pointer', background: 'var(--bg-3)',
              border: '1px solid var(--line)', color: 'var(--text)',
              fontFamily: 'var(--sans)',
            }}
          >Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={confirmState === 'polling'}
            style={{
              flex: 2, padding: '12px 0', fontSize: 14, fontWeight: 700,
              borderRadius: 12, border: 'none', cursor: confirmState === 'polling' ? 'not-allowed' : 'pointer',
              background: confirmState === 'polling' ? 'rgba(244,183,40,.5)' : 'var(--gold)',
              color: '#1a1003',
              fontFamily: 'var(--display)',
              boxShadow: '0 4px 16px rgba(244,183,40,.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
              opacity: confirmState === 'polling' ? 0.7 : 1,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <path d="M12 2l8 3.5V11c0 5-3.4 8.6-8 10-4.6-1.4-8-5-8-10V5.5z" />
            </svg>
            {confirmState === 'polling' ? 'Checking…' : 'Confirm & Buy'}
          </button>
        </div>

        {/* ── Inline confirmation status (below button) ── */}
        {confirmState === 'polling' && (
          <div style={{
            background: 'rgba(244,183,40,.06)',
            border: '1px solid rgba(244,183,40,.2)',
            borderRadius: 12, padding: '16px',
            animation: 'fadeIn .3s ease',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(244,183,40,.1)',
                border: '2px solid rgba(244,183,40,.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'pulse 1.5s ease-in-out infinite',
                flexShrink: 0,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f4b728" strokeWidth="2" aria-hidden="true">
                  <path d="M12 2l8 3.5V11c0 5-3.4 8.6-8 10-4.6-1.4-8-5-8-10V5.5z" />
                </svg>
              </div>
              <div>
                <div style={{ fontFamily: 'var(--display)', fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Checking Zcash Network…</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-faint)' }}>
                  Polling blockchain · attempt {pollAttempts + 1}
                </div>
              </div>
            </div>
            <div style={{ height: 5, borderRadius: 999, background: 'rgba(228,228,229,.07)', overflow: 'hidden', marginBottom: 8 }}>
              <div style={{
                height: '100%',
                width: `${Math.min((pollAttempts / 10) * 100, 90)}%`,
                background: 'linear-gradient(90deg, var(--gold), #ffe066)',
                borderRadius: 999,
                transition: 'width 1s ease',
                boxShadow: '0 0 8px rgba(244,183,40,.4)',
              }} />
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'rgba(228,228,229,.3)' }}>
              ⛓ Waiting for 1 confirmation on Zcash mainnet
            </div>
          </div>
        )}

        {confirmState === 'not_found' && (
          <div style={{
            background: 'rgba(251,191,36,.06)',
            border: '1px solid rgba(251,191,36,.25)',
            borderRadius: 12, padding: '14px 16px',
            animation: 'fadeIn .3s ease',
          }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: '#fbbf24', marginBottom: 4 }}>⚠ Transaction not found yet</div>
            <div style={{ fontSize: 12, color: 'var(--text-faint)', lineHeight: 1.5 }}>
              Your transaction hasn&apos;t appeared on-chain yet. Make sure you&apos;ve sent the ZEC and the txid is correct. We&apos;ll keep checking automatically.
            </div>
            <button
              onClick={handleConfirm}
              style={{ marginTop: 10, padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(244,183,40,.3)', background: 'rgba(244,183,40,.1)', color: '#f4b728', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer' }}
            >
              Retry check
            </button>
          </div>
        )}

        {confirmState === 'confirmed' && (
          <div style={{
            background: 'rgba(74,222,128,.06)',
            border: '1px solid rgba(74,222,128,.3)',
            borderRadius: 12, padding: '16px',
            animation: 'fadeIn .3s ease',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(74,222,128,.12)', border: '2px solid rgba(74,222,128,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>✓</div>
              <div>
                <div style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 15, color: '#4ade80', marginBottom: 2 }}>Transaction Confirmed!</div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 10, color: '#4ade80', background: 'rgba(74,222,128,.08)', border: '1px solid rgba(74,222,128,.2)', borderRadius: 999, padding: '3px 10px' }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
                  {confirmations} confirmation{confirmations !== 1 ? 's' : ''} · On-chain
                </div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-faint)', lineHeight: 1.5, marginBottom: 12 }}>
              Your ZEC is on-chain. <strong style={{ color: 'var(--text)' }}>{itemName}</strong> will be transferred to your shielded address shortly.
            </div>
            <button
              onClick={() => setDone(true)}
              style={{ width: '100%', padding: '11px 0', fontSize: 14, fontWeight: 700, borderRadius: 12, border: 'none', cursor: 'pointer', background: 'var(--gold)', color: '#1a1003', fontFamily: 'var(--display)' }}
            >
              Done 🎉
            </button>
          </div>
        )}

        {confirmState === 'error' && (
          <div style={{
            background: 'rgba(248,113,113,.06)',
            border: '1px solid rgba(248,113,113,.25)',
            borderRadius: 12, padding: '14px 16px',
            animation: 'fadeIn .3s ease',
          }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: '#f87171', marginBottom: 4 }}>⚠ Network error</div>
            <div style={{ fontSize: 12, color: 'var(--text-faint)', lineHeight: 1.5 }}>
              Could not reach the Zcash explorer. Check your connection and try again.
            </div>
            <button
              onClick={handleConfirm}
              style={{ marginTop: 10, padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(248,113,113,.3)', background: 'rgba(248,113,113,.1)', color: '#f87171', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer' }}
            >
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
